import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Phone, Mail, Filter, Users, Building2, X, Stethoscope } from "lucide-react";
import { toast } from "sonner";

const PROFESSION_ICON = {
  Médecin: "🩺",
  Pharmacien: "💊",
  "Chirurgien-dentiste": "🦷",
  "Sage-femme": "👶",
  Infirmier: "💉",
  "Masseur-kinésithérapeute": "💆",
  "Pédicure-podologue": "🦶",
  Orthophoniste: "🗣️",
  Ostéopathe: "🌿",
  Psychologue: "🧠",
  Diététicien: "🥗",
  "Manipulateur d'électroradiologie": "📡",
  Ambulancier: "🚑",
  "Opticien-lunetier": "👓",
  Audioprothésiste: "🎧",
};

function ProCard({ pro }) {
  const icon = PROFESSION_ICON[pro.profession] || "🩺";
  return (
    <Link to={`/pro/${pro.rpps}`} className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-[#618DB8] hover:shadow-sm transition" data-testid={`pro-${pro.rpps}`}>
      <div className="flex gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
          {pro.first_name?.charAt(0) || "?"}{pro.last_name?.charAt(0) || ""}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-deep">{pro.display_name}</span>
            <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 font-semibold">{icon} {pro.profession}</span>
          </div>
          {pro.specialty && pro.specialty !== pro.profession && (
            <div className="text-sm text-[#618DB8] font-semibold mt-0.5">{pro.specialty}</div>
          )}
          {pro.facility && <div className="text-xs text-slate-500 mt-0.5">{pro.facility}</div>}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
            {(pro.address || pro.city) && (
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[pro.address, pro.zip_code, pro.city].filter(Boolean).join(", ")}</span>
            )}
            {pro.phone && <a href={`tel:${pro.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-[#618DB8]"><Phone className="h-3.5 w-3.5" />{pro.phone}</a>}
            {pro.email && <a href={`mailto:${pro.email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-[#618DB8]"><Mail className="h-3.5 w-3.5" />{pro.email}</a>}
          </div>
        </div>
      </div>
    </Link>
  );
}

function FacilityCard({ f }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-[#618DB8] hover:shadow-sm transition" data-testid={`facility-${f.finess}`}>
      <div className="flex gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center flex-shrink-0">
          <Building2 className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-deep">{f.name}</div>
          <div className="text-sm text-emerald-700 font-semibold mt-0.5">{f.type_label}</div>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[f.address, f.zip_code, f.city].filter(Boolean).join(", ")}</span>
            {f.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{f.phone}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Directory() {
  const [tab, setTab] = useState("pros");
  const [q, setQ] = useState("");
  const [profession, setProfession] = useState("");
  const [type_label, setTypeLabel] = useState("");
  const [city, setCity] = useState("");
  const [pros, setPros] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [totalPros, setTotalPros] = useState(0);
  const [totalFac, setTotalFac] = useState(0);
  const [professions, setProfessions] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/directory/pros/professions").then(({ data }) => setProfessions(data.professions || [])).catch(() => {});
    api.get("/directory/facilities/types").then(({ data }) => setTypes(data.types || [])).catch(() => {});
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      if (tab === "pros") {
        const { data } = await api.get("/directory/pros", { params: { q, profession, city, limit: 60 } });
        setPros(data.items); setTotalPros(data.total);
      } else {
        const { data } = await api.get("/directory/facilities", { params: { q, type_label, city, limit: 60 } });
        setFacilities(data.items); setTotalFac(data.total);
      }
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, [tab, profession, type_label]);
  const onSubmit = (e) => { e.preventDefault(); search(); };
  const clearAll = () => { setQ(""); setProfession(""); setTypeLabel(""); setCity(""); setTimeout(search, 100); };

  const activeFilters = [profession, type_label, city].filter(Boolean).length + (q ? 1 : 0);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Annuaire national · source ANS / data.gouv</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-deep tracking-tight">Annuaire des professionnels de santé</h1>
        <p className="text-sm text-slate-500 mt-1">
          Tous les professionnels de santé et établissements enregistrés en France · Consultation gratuite pour tous
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button onClick={() => setTab("pros")} className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === "pros" ? "border-[#618DB8] text-[#618DB8]" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid="tab-directory-pros">
          <Users className="h-4 w-4 inline me-1.5" />Professionnels ({totalPros})
        </button>
        <button onClick={() => setTab("facilities")} className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${tab === "facilities" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid="tab-directory-facilities">
          <Building2 className="h-4 w-4 inline me-1.5" />Établissements ({totalFac})
        </button>
      </div>

      {/* Search + Filters */}
      <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 shadow-sm">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === "pros" ? "Nom, prénom du professionnel…" : "Nom de l'établissement…"} className="pl-9" data-testid="directory-search" />
          </div>
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville" className="max-w-[160px]" data-testid="directory-city" />
          {tab === "pros" ? (
            <select value={profession} onChange={(e) => setProfession(e.target.value)} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm min-w-[180px]" data-testid="directory-profession">
              <option value="">Toutes professions</option>
              {professions.map((p) => <option key={p} value={p}>{PROFESSION_ICON[p] || "🩺"} {p}</option>)}
            </select>
          ) : (
            <select value={type_label} onChange={(e) => setTypeLabel(e.target.value)} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm min-w-[200px]" data-testid="directory-type">
              <option value="">Tous types</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <Button type="submit" className="btn-primary" data-testid="directory-submit">
            <Search className="h-4 w-4 me-1" />Rechercher
          </Button>
          {activeFilters > 0 && (
            <Button type="button" variant="ghost" onClick={clearAll} className="text-slate-500" data-testid="directory-clear">
              <X className="h-4 w-4 me-1" />Réinitialiser
            </Button>
          )}
        </div>
        {activeFilters > 0 && (
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
            <Filter className="h-3 w-3" />
            {activeFilters} filtre{activeFilters > 1 ? "s" : ""} actif{activeFilters > 1 ? "s" : ""}
          </div>
        )}
      </form>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement…</div>
      ) : tab === "pros" ? (
        pros.length === 0 ? (
          <div className="text-center py-12 text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl">
            <Stethoscope className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            Aucun professionnel trouvé pour ces critères.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pros.map((p) => <ProCard key={p.rpps} pro={p} />)}
          </div>
        )
      ) : (
        facilities.length === 0 ? (
          <div className="text-center py-12 text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl">
            <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            Aucun établissement trouvé.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {facilities.map((f) => <FacilityCard key={f.finess} f={f} />)}
          </div>
        )
      )}

      <div className="mt-8 text-[11px] text-slate-400 leading-relaxed max-w-3xl">
        <strong>Source :</strong> Répertoire Partagé des Professionnels de Santé (RPPS) — Agence du Numérique en Santé (ANS) ·
        Fichier National des Établissements Sanitaires et Sociaux (FINESS) — data.gouv.fr · Licence Etalab. Cet annuaire est mis à jour quotidiennement.
      </div>
    </div>
  );
}
